import type { OrderRow } from '../../api/amazonconnect';
import type { DayWindow } from '../daily-brief/dayWindows';
import { filterOrders } from '../daily-brief/aggregateOrders';
import { parseOrderTotal, pctChange } from '../daily-brief/format';
import { trailingDayWindows } from '../executive-kpis/periodWindows';
import { extractOrderGeo } from './extractOrderGeo';
import { INDIA_STATES } from './indiaStates';
import { normalizeIndiaState } from './normalizeState';
import type {
  CityRow,
  DayPoint,
  IndiaMapModel,
  IndiaStateCode,
  InsightItem,
  MapLayerId,
  ProductRow,
  StateAggregate,
} from './types';

const COVERAGE_THRESHOLD = 0.4;

type ProdAcc = { orders: number; units: number; unitsN: number; gmv: number; gmvN: number };
type CityAcc = { orders: number; gmv: number; gmvN: number };
type DayAcc = { orders: number; gmv: number; gmvN: number; units: number; unitsN: number };

type Bucket = {
  orderCount: number;
  ordersWithTotal: number;
  gmvSum: number;
  unitsSum: number;
  ordersWithUnits: number;
  currencies: Set<string>;
  lastUpdated: string | null;
  lastSync: string | null;
  products: Map<string, ProdAcc>;
  cities: Map<string, CityAcc>;
  /** iso day → day acc (local calendar via purchase_date string prefix best-effort) */
  days: Map<string, DayAcc>;
};

function emptyBucket(): Bucket {
  return {
    orderCount: 0,
    ordersWithTotal: 0,
    gmvSum: 0,
    unitsSum: 0,
    ordersWithUnits: 0,
    currencies: new Set(),
    lastUpdated: null,
    lastSync: null,
    products: new Map(),
    cities: new Map(),
    days: new Map(),
  };
}

function emptyDay(): DayAcc {
  return { orders: 0, gmv: 0, gmvN: 0, units: 0, unitsN: 0 };
}

function bumpLast(cur: string | null, next?: string): string | null {
  if (!next) return cur;
  if (!cur) return next;
  return next > cur ? next : cur;
}

function dayKey(purchase?: string): string | null {
  if (!purchase) return null;
  // Prefer ISO date portion; local-ish enough for scan aggregation
  const m = purchase.match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

function bumpProd(map: Map<string, ProdAcc>, key: string, units: number | null, gmv: number | null) {
  let p = map.get(key);
  if (!p) {
    p = { orders: 0, units: 0, unitsN: 0, gmv: 0, gmvN: 0 };
    map.set(key, p);
  }
  p.orders += 1;
  if (units != null) {
    p.units += units;
    p.unitsN += 1;
  }
  if (gmv != null) {
    p.gmv += gmv;
    p.gmvN += 1;
  }
}

function bumpCity(map: Map<string, CityAcc>, city: string, gmv: number | null) {
  let c = map.get(city);
  if (!c) {
    c = { orders: 0, gmv: 0, gmvN: 0 };
    map.set(city, c);
  }
  c.orders += 1;
  if (gmv != null) {
    c.gmv += gmv;
    c.gmvN += 1;
  }
}

function bumpDay(map: Map<string, DayAcc>, iso: string, units: number | null, gmv: number | null) {
  let d = map.get(iso);
  if (!d) {
    d = emptyDay();
    map.set(iso, d);
  }
  d.orders += 1;
  if (gmv != null) {
    d.gmv += gmv;
    d.gmvN += 1;
  }
  if (units != null) {
    d.units += units;
    d.unitsN += 1;
  }
}

function accumulateState(b: Bucket, o: OrderRow, multiAnywhere: boolean) {
  b.orderCount += 1;
  b.lastUpdated = bumpLast(b.lastUpdated, o.purchase_date ?? o.last_sync ?? null);
  b.lastSync = bumpLast(b.lastSync, o.last_sync ?? o.purchase_date ?? null);

  const total = parseOrderTotal(o.order_total);
  if (total != null) {
    b.ordersWithTotal += 1;
    b.gmvSum += total;
    if (o.currency) b.currencies.add(o.currency.toUpperCase());
  }

  let unitsAdd: number | null = null;
  if (typeof o.quantity === 'number' && o.quantity > 0) {
    b.ordersWithUnits += 1;
    b.unitsSum += o.quantity;
    unitsAdd = o.quantity;
  }

  const prod = (o.asin || o.sku || '').trim();
  if (prod) bumpProd(b.products, prod, unitsAdd, multiAnywhere ? null : total);

  const geo = extractOrderGeo(o);
  if (geo.city) bumpCity(b.cities, geo.city.toUpperCase(), multiAnywhere ? null : total);

  const dk = dayKey(o.purchase_date);
  if (dk) bumpDay(b.days, dk, unitsAdd, multiAnywhere ? null : total);
}

function topProducts(map: Map<string, ProdAcc>, stateGmv: number | null, multi: boolean): ProductRow[] {
  return [...map.entries()]
    .sort((a, b) => b[1].orders - a[1].orders || a[0].localeCompare(b[0]))
    .slice(0, 8)
    .map(([key, p]) => {
      const gmv = multi || p.gmvN === 0 ? null : p.gmv;
      const units = p.unitsN === 0 ? null : p.units;
      const contributionPct =
        gmv != null && stateGmv != null && stateGmv > 0
          ? Math.round((gmv / stateGmv) * 1000) / 10
          : null;
      return { key, orders: p.orders, units, gmv, contributionPct };
    });
}

function topCities(map: Map<string, CityAcc>, stateGmv: number | null, multi: boolean): CityRow[] {
  return [...map.entries()]
    .sort((a, b) => b[1].orders - a[1].orders || a[0].localeCompare(b[0]))
    .slice(0, 8)
    .map(([city, c]) => {
      const gmv = multi || c.gmvN === 0 ? null : c.gmv;
      const contributionPct =
        gmv != null && stateGmv != null && stateGmv > 0
          ? Math.round((gmv / stateGmv) * 1000) / 10
          : null;
      return { city, orders: c.orders, gmv, contributionPct };
    });
}

function seriesFromDays(
  daysMap: Map<string, DayAcc>,
  windows: DayWindow[],
  multi: boolean,
): DayPoint[] {
  return windows.map((w) => {
    const d = daysMap.get(w.isoFrom);
    if (!d || d.orders === 0) {
      return { iso: w.isoFrom, gmv: 0, orders: 0, units: 0 };
    }
    return {
      iso: w.isoFrom,
      gmv: multi ? null : d.gmvN === 0 ? null : d.gmv,
      orders: d.orders,
      units: d.unitsN === 0 ? null : d.units,
    };
  });
}

function confidenceForCoverage(pct: number | null): StateAggregate['confidence'] {
  if (pct == null) return 'none';
  if (pct >= 70) return 'high';
  if (pct >= 40) return 'medium';
  if (pct > 0) return 'low';
  return 'none';
}

function layerValue(s: StateAggregate, layer: MapLayerId): number {
  if (layer === 'sales') return s.gmv ?? 0;
  if (layer === 'orders') return s.orderCount;
  return s.units ?? 0;
}

function buildInsights(states: StateAggregate[], multi: boolean): InsightItem[] {
  const live = states.filter((s) => s.hasData);
  if (live.length === 0) {
    return [
      {
        id: 'no-geo',
        tone: 'info',
        text: 'No LIVE ship_state coverage in this period sample — map stays honest-empty.',
      },
    ];
  }
  const out: InsightItem[] = [];

  const byGmv = [...live]
    .filter((s) => s.gmv != null)
    .sort((a, b) => (b.gmv ?? 0) - (a.gmv ?? 0));
  if (byGmv[0] && byGmv[0].contributionPct != null) {
    out.push({
      id: 'top-contrib',
      tone: 'positive',
      text: `${byGmv[0].name} leads geo-known GMV (${byGmv[0].contributionPct.toFixed(1)}% contribution).`,
    });
  }

  const growers = [...live]
    .filter((s) => s.growthPct != null && Number.isFinite(s.growthPct))
    .sort((a, b) => (b.growthPct ?? 0) - (a.growthPct ?? 0));
  if (growers[0] && (growers[0].growthPct ?? 0) > 2) {
    out.push({
      id: 'fastest',
      tone: 'positive',
      text: `${growers[0].name} is fastest growing vs compare window (${growers[0].growthPct!.toFixed(1)}% GMV).`,
    });
  }
  if (growers.length && (growers[growers.length - 1].growthPct ?? 0) < -2) {
    const d = growers[growers.length - 1];
    out.push({
      id: 'declining',
      tone: 'watch',
      text: `${d.name} GMV declined ${d.growthPct!.toFixed(1)}% vs compare window.`,
    });
  }

  const highAov = [...live]
    .filter((s) => s.aov != null)
    .sort((a, b) => (b.aov ?? 0) - (a.aov ?? 0));
  if (highAov[0]?.aov != null) {
    out.push({
      id: 'high-aov',
      tone: 'info',
      text: `${highAov[0].name} shows highest AOV among states with parseable totals.`,
    });
  }

  if (multi) {
    out.push({
      id: 'multi-ccy',
      tone: 'watch',
      text: 'Multiple currencies in window — GMV/AOV geo totals marked Unavailable.',
    });
  }

  // Cap for 10-second scan
  return out.slice(0, 6);
}

export function buildIndiaMapModel(opts: {
  connectionId: string;
  sellerLabel: string;
  marketplace: string;
  period: DayWindow;
  compareLabel: string | null;
  /** Prior period for growth % — only when dashboard compare is active. */
  priorPeriod?: DayWindow | null;
  orders: OrderRow[];
  truncated: boolean;
  asOf?: Date;
  rankLayer?: MapLayerId;
}): IndiaMapModel {
  const {
    connectionId,
    sellerLabel,
    marketplace,
    period,
    compareLabel,
    priorPeriod = null,
    orders,
    truncated,
    asOf = new Date(),
    rankLayer = 'sales',
  } = opts;

  // Currency plurality across all known-total orders in window
  const inPeriod = filterOrders(orders, period);
  const allCcy = new Set<string>();
  for (const o of inPeriod) {
    if (o.currency) allCcy.add(o.currency.toUpperCase());
  }
  const multiAnywhere = allCcy.size > 1;

  const buckets = new Map<IndiaStateCode, Bucket>();
  for (const s of INDIA_STATES) buckets.set(s.code, emptyBucket());

  let withState = 0;
  for (const o of inPeriod) {
    const geo = extractOrderGeo(o);
    if (!geo.stateCode) continue;
    withState += 1;
    accumulateState(buckets.get(geo.stateCode)!, o, multiAnywhere);
  }

  // Prior period GMV by state for growth
  const priorGmv = new Map<IndiaStateCode, number | null>();
  if (priorPeriod) {
    const priorOrders = filterOrders(orders, priorPeriod);
    const priorB = new Map<IndiaStateCode, { gmv: number; n: number; ccy: Set<string> }>();
    for (const s of INDIA_STATES) priorB.set(s.code, { gmv: 0, n: 0, ccy: new Set() });
    for (const o of priorOrders) {
      const geo = extractOrderGeo(o);
      if (!geo.stateCode) continue;
      const total = parseOrderTotal(o.order_total);
      if (total == null) continue;
      const b = priorB.get(geo.stateCode)!;
      b.gmv += total;
      b.n += 1;
      if (o.currency) b.ccy.add(o.currency.toUpperCase());
    }
    for (const [code, b] of priorB) {
      if (b.n === 0) priorGmv.set(code, 0);
      else if (b.ccy.size > 1 || multiAnywhere) priorGmv.set(code, null);
      else priorGmv.set(code, b.gmv);
    }
  }

  const totalOrders = inPeriod.length;
  const addressCoveragePct =
    totalOrders === 0 ? null : Math.round((withState / totalOrders) * 1000) / 10;
  const heatGated = totalOrders > 0 && withState / totalOrders < COVERAGE_THRESHOLD;
  const conf = confidenceForCoverage(addressCoveragePct);

  const trailEnd = period.end;
  const w7 = trailingDayWindows(trailEnd, 7);
  const w15 = trailingDayWindows(trailEnd, 15);
  const w30 = trailingDayWindows(trailEnd, 30);

  let totalGmvKnown = 0;
  let gmvOk = !multiAnywhere;
  let totalUnitsKnown = 0;
  let unitsOk = true;
  let totalOrdersKnown = 0;

  const states: StateAggregate[] = INDIA_STATES.map((def) => {
    const b = buckets.get(def.code)!;
    totalOrdersKnown += b.orderCount;
    const stateMulti = b.currencies.size > 1 || multiAnywhere;

    let gmv: number | null = null;
    if (b.orderCount === 0) gmv = 0;
    else if (stateMulti) {
      gmv = null;
      gmvOk = false;
    } else if (b.ordersWithTotal === 0) gmv = null;
    else {
      gmv = b.gmvSum;
      totalGmvKnown += b.gmvSum;
    }

    let units: number | null = null;
    if (b.orderCount === 0) units = 0;
    else if (b.ordersWithUnits === 0) {
      units = null;
      unitsOk = false;
    } else {
      units = b.unitsSum;
      totalUnitsKnown += b.unitsSum;
    }

    const aov =
      b.ordersWithTotal > 0 && !stateMulti ? b.gmvSum / b.ordersWithTotal : null;
    const asp =
      b.ordersWithTotal > 0 && b.ordersWithUnits > 0 && !stateMulti && b.unitsSum > 0
        ? b.gmvSum / b.unitsSum
        : null;

    const prior = priorGmv.get(def.code);
    const growthPct =
      priorPeriod && gmv != null && prior != null ? pctChange(gmv, prior) : null;

    const coveragePct =
      b.orderCount === 0
        ? null
        : Math.round((b.ordersWithTotal / b.orderCount) * 1000) / 10;

    return {
      code: def.code,
      name: def.name,
      orderCount: b.orderCount,
      ordersWithTotal: b.ordersWithTotal,
      gmv,
      units,
      ordersWithUnits: b.ordersWithUnits,
      currencies: [...b.currencies],
      contributionPct: null,
      shareOfKnownGeoPct: null,
      nationalRank: null,
      aov,
      asp,
      growthPct,
      lastUpdated: b.lastUpdated,
      lastSync: b.lastSync,
      coveragePct,
      confidence: b.orderCount > 0 ? conf : 'none',
      topProducts: topProducts(b.products, gmv, stateMulti),
      topCities: topCities(b.cities, gmv, stateMulti),
      trend7: seriesFromDays(b.days, w7, stateMulti),
      trend15: seriesFromDays(b.days, w15, stateMulti),
      trend30: seriesFromDays(b.days, w30, stateMulti),
      hasData: b.orderCount > 0,
    };
  });

  const gmvDenom =
    gmvOk && !multiAnywhere ? states.reduce((s, x) => s + (x.gmv ?? 0), 0) : null;

  for (const s of states) {
    if (!s.hasData) {
      s.contributionPct = null;
      s.shareOfKnownGeoPct = null;
      continue;
    }
    if (gmvDenom != null && gmvDenom > 0 && s.gmv != null) {
      s.contributionPct = Math.round((s.gmv / gmvDenom) * 1000) / 10;
      s.shareOfKnownGeoPct = s.contributionPct;
    } else if (totalOrdersKnown > 0) {
      s.shareOfKnownGeoPct = Math.round((s.orderCount / totalOrdersKnown) * 1000) / 10;
      s.contributionPct = s.shareOfKnownGeoPct;
    }
  }

  // National rank by sales (GMV then orders) among LIVE states
  const rankedList = [...states]
    .filter((s) => s.hasData)
    .sort(
      (a, b) =>
        (b.gmv ?? -1) - (a.gmv ?? -1) ||
        b.orderCount - a.orderCount ||
        a.name.localeCompare(b.name),
    );
  rankedList.forEach((s, i) => {
    s.nationalRank = i + 1;
  });

  const ranked = states
    .filter((s) => s.hasData)
    .sort(
      (a, b) =>
        layerValue(b, rankLayer) - layerValue(a, rankLayer) || a.name.localeCompare(b.name),
    )
    .map((s) => s.code);

  let currency: string | undefined;
  if (allCcy.size === 1) currency = [...allCcy][0];

  return {
    connectionId,
    sellerLabel,
    marketplace,
    periodLabel: period.label,
    compareLabel,
    periodFrom: period.isoFrom,
    periodTo: period.isoTo,
    asOf: asOf.toISOString(),
    truncated,
    totalOrdersInPeriod: totalOrders,
    ordersWithShipState: withState,
    addressCoveragePct,
    heatGated,
    gateReason: heatGated
      ? 'Too few orders in this period carry a delivery state, so the heat map stays off.'
      : null,
    multiCurrency: multiAnywhere,
    totalGmvKnownGeo: multiAnywhere || !gmvOk ? null : totalGmvKnown,
    totalOrdersKnownGeo: totalOrdersKnown,
    totalUnitsKnownGeo: unitsOk ? totalUnitsKnown : null,
    currency,
    states,
    ranked,
    topCities: [],
    insights: buildInsights(states, multiAnywhere),
    warehouses: [],
    warehousePins: [],
    warehouseCoverageStates: [],
    warehouseMasterTotal: 0,
    warehouseMissingCoords: 0,
    warehouseLoadError: null,
  };
}

export type ReportGeoInput = {
  cities: Array<{ city: string; state: string; orders: number; units: number; sales: number }>;
  states: Array<{ city?: string; state: string; orders: number; units: number; sales: number }>;
  currency?: string;
  line_count: number;
  source?: string;
};

function emptyTrend(windows: DayWindow[]): DayPoint[] {
  return windows.map((w) => ({ iso: w.isoFrom, gmv: 0, orders: 0, units: 0 }));
}

/** India map from All Orders ship-city / ship-state rollups (not Orders API scan). */
export function buildIndiaMapFromReportGeo(opts: {
  connectionId: string;
  sellerLabel: string;
  marketplace: string;
  period: DayWindow;
  compareLabel: string | null;
  geo: ReportGeoInput;
  prior?: ReportGeoInput | null;
  asOf?: Date;
  rankLayer?: MapLayerId;
}): IndiaMapModel {
  const {
    connectionId,
    sellerLabel,
    marketplace,
    period,
    compareLabel,
    geo,
    prior = null,
    asOf = new Date(),
    rankLayer = 'sales',
  } = opts;

  const currency = geo.currency || undefined;
  const trailEnd = period.end;
  const w7 = trailingDayWindows(trailEnd, 7);
  const w15 = trailingDayWindows(trailEnd, 15);
  const w30 = trailingDayWindows(trailEnd, 30);
  const blankTrend7 = emptyTrend(w7);
  const blankTrend15 = emptyTrend(w15);
  const blankTrend30 = emptyTrend(w30);

  const byCode = new Map<IndiaStateCode, { orders: number; units: number; sales: number }>();
  for (const s of INDIA_STATES) byCode.set(s.code, { orders: 0, units: 0, sales: 0 });

  let mappedOrders = 0;
  let listedOrders = 0;
  for (const row of geo.states) {
    listedOrders += row.orders;
    const code = normalizeIndiaState(row.state);
    if (!code) continue;
    const b = byCode.get(code)!;
    b.orders += row.orders;
    b.units += row.units;
    b.sales += row.sales;
    mappedOrders += row.orders;
  }

  const citiesByState = new Map<IndiaStateCode, CityRow[]>();
  const nationalCities: CityRow[] = [];
  const salesTotal = geo.cities.reduce((s, c) => s + c.sales, 0);
  const sortedCities = [...geo.cities].sort((a, b) => b.sales - a.sales || a.city.localeCompare(b.city));
  for (const c of sortedCities) {
    const code = normalizeIndiaState(c.state);
    const row: CityRow = {
      city: c.city,
      state: c.state,
      orders: c.orders,
      units: c.units,
      gmv: c.sales,
      contributionPct:
        salesTotal > 0 ? Math.round((c.sales / salesTotal) * 1000) / 10 : null,
    };
    nationalCities.push(row);
    if (!code) continue;
    const list = citiesByState.get(code) ?? [];
    if (list.length < 8) list.push(row);
    citiesByState.set(code, list);
  }

  const priorSales = new Map<IndiaStateCode, number>();
  if (prior) {
    for (const row of prior.states) {
      const code = normalizeIndiaState(row.state);
      if (!code) continue;
      priorSales.set(code, (priorSales.get(code) ?? 0) + row.sales);
    }
  }

  const gmvDenom = [...byCode.values()].reduce((s, b) => s + b.sales, 0);
  const totalUnits = [...byCode.values()].reduce((s, b) => s + b.units, 0);

  const states: StateAggregate[] = INDIA_STATES.map((def) => {
    const b = byCode.get(def.code)!;
    const hasData = b.orders > 0;
    const gmv = hasData ? b.sales : 0;
    const units = hasData ? b.units : 0;
    const priorG = prior ? (priorSales.get(def.code) ?? 0) : null;
    const growthPct =
      prior && gmv != null && priorG != null ? pctChange(gmv, priorG) : null;
    return {
      code: def.code,
      name: def.name,
      orderCount: b.orders,
      ordersWithTotal: b.orders,
      gmv,
      units,
      ordersWithUnits: b.orders,
      currencies: currency ? [currency] : [],
      contributionPct:
        hasData && gmvDenom > 0 ? Math.round((b.sales / gmvDenom) * 1000) / 10 : null,
      shareOfKnownGeoPct:
        hasData && mappedOrders > 0 ? Math.round((b.orders / mappedOrders) * 1000) / 10 : null,
      nationalRank: null,
      aov: hasData && b.orders > 0 ? b.sales / b.orders : null,
      asp: hasData && b.units > 0 ? b.sales / b.units : null,
      growthPct,
      lastUpdated: null,
      lastSync: null,
      coveragePct: hasData ? 100 : null,
      confidence: hasData ? 'high' : 'none',
      topProducts: [],
      topCities: citiesByState.get(def.code) ?? [],
      trend7: blankTrend7,
      trend15: blankTrend15,
      trend30: blankTrend30,
      hasData,
    };
  });

  const rankedList = [...states]
    .filter((s) => s.hasData)
    .sort(
      (a, b) =>
        (b.gmv ?? -1) - (a.gmv ?? -1) ||
        b.orderCount - a.orderCount ||
        a.name.localeCompare(b.name),
    );
  rankedList.forEach((s, i) => {
    s.nationalRank = i + 1;
  });

  const ranked = states
    .filter((s) => s.hasData)
    .sort(
      (a, b) =>
        layerValue(b, rankLayer) - layerValue(a, rankLayer) || a.name.localeCompare(b.name),
    )
    .map((s) => s.code);

  const addressCoveragePct =
    listedOrders === 0 ? null : Math.round((mappedOrders / listedOrders) * 1000) / 10;
  const heatGated = geo.line_count > 0 && mappedOrders === 0;

  return {
    connectionId,
    sellerLabel,
    marketplace,
    periodLabel: period.label,
    compareLabel,
    periodFrom: period.isoFrom,
    periodTo: period.isoTo,
    asOf: asOf.toISOString(),
    truncated: false,
    totalOrdersInPeriod: listedOrders,
    ordersWithShipState: mappedOrders,
    addressCoveragePct,
    heatGated,
    gateReason: heatGated
      ? 'Orders have city names Amazon sent, but none matched an India state label for the heat map.'
      : null,
    multiCurrency: false,
    totalGmvKnownGeo: gmvDenom,
    totalOrdersKnownGeo: mappedOrders,
    totalUnitsKnownGeo: totalUnits,
    currency,
    states,
    ranked,
    topCities: nationalCities.slice(0, 25),
    source: geo.source,
    insights: buildInsights(states, false),
    warehouses: [],
    warehousePins: [],
    warehouseCoverageStates: [],
    warehouseMasterTotal: 0,
    warehouseMissingCoords: 0,
    warehouseLoadError: null,
  };
}

export function layerMax(states: StateAggregate[], layer: MapLayerId): number {
  let m = 0;
  for (const s of states) {
    if (!s.hasData) continue;
    m = Math.max(m, layerValue(s, layer));
  }
  return m;
}

export function stateMetric(s: StateAggregate, layer: MapLayerId): number | null {
  if (!s.hasData) return null;
  if (layer === 'sales') return s.gmv;
  if (layer === 'orders') return s.orderCount;
  return s.units;
}
